import { CircularProgress, FormControlLabel, Switch } from '@mui/material';
import { useSnackbar } from 'notistack';

import {
  useGetAdminMembershipInfoQuery,
  useUpdateAdminMembershipInfoMutation,
} from '../store/hooks';

interface PrescribingProxyToggleProps {
  organizationId: string;
  userId: string;
}

/**
 * Per-member toggle for the "prepare prescriptions on behalf" capability
 * (GDN-1119) — the prescribing proxy. Writes the membership-info flag via the
 * admin endpoint. The capability is role-gated to operations_support members
 * server-side, so the switch reflects the effective stored state the update
 * echoes back (turning it on for a non-operations_support member is a no-op,
 * surfaced as a warning rather than a silent failure).
 */
export function PrescribingProxyToggle({
  organizationId,
  userId,
}: PrescribingProxyToggleProps) {
  const { data, isLoading } = useGetAdminMembershipInfoQuery(
    { organizationId, userId },
    { skip: !organizationId || !userId },
  );
  const [updateMembershipInfo, { isLoading: isUpdating }] =
    useUpdateAdminMembershipInfoMutation();
  const { enqueueSnackbar } = useSnackbar();

  const handleToggle = async (checked: boolean) => {
    try {
      const res = await updateMembershipInfo({
        organizationId,
        userId,
        updateMembershipInfoRequest: {
          can_prepare_prescriptions_on_behalf: checked,
        },
      }).unwrap();
      const effective = res.can_prepare_prescriptions_on_behalf;
      if (checked && !effective) {
        enqueueSnackbar(
          'Prescribing proxy requires the operations_support role',
          { variant: 'warning' },
        );
      } else {
        enqueueSnackbar(
          effective ? 'Prescribing proxy enabled' : 'Prescribing proxy disabled',
          { variant: 'success' },
        );
      }
    } catch {
      enqueueSnackbar('Failed to update prescribing proxy setting', {
        variant: 'error',
      });
    }
  };

  if (isLoading) return <CircularProgress size={16} />;

  return (
    <FormControlLabel
      control={
        <Switch
          checked={data?.can_prepare_prescriptions_on_behalf ?? false}
          onChange={e => void handleToggle(e.target.checked)}
          disabled={isUpdating}
          size="small"
        />
      }
      label={<span className="text-sm text-gray-700">Prescribing proxy</span>}
    />
  );
}
